import {
  CustomerDetailHeaderFieldsFragment,
  CustomerDetailRowFieldsFragment,
  PricingCustomerDetailDataQuery,
  VariantPackCostType,
} from '@/types';
import { getLocalizedPackUnit } from '@/utils/item';
import { isValidNumber } from '@/utils/number';
import { PackUnit } from '@/enums';
import {
  PriceEditDisplayCell,
  toPriceEditDisplayMap,
} from '@sales-portal/blocs/utils/price-edit-display';
import {
  PricingAccountRow,
  WeeklyPurchaseQuantity,
} from '@sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { PricingCalculations } from '@/utils/pricing-calculations';
import {
  PricingEdit,
  PricingInvoiceHistoryEntry,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { toPricingInvoiceHistory } from '@sales-portal/domain/pricing/blocs/transformers/pricing-invoice-history';
import { useMemo } from 'react';

export interface PricingCustomerDetailRow {
  costDeltaInMicros: number | null;
  costInMicros: number | null;
  editsGrossProfitImpactInMicros: number;
  grossMarginDeltaInMicros: number | null;
  grossMarginPercentage: number | null;
  grossMarginPercentageDelta: number | null;
  grossProfitImpactInMicros: number | null;
  grossProfitPerUnitInMicros: number | null;
  invoiceHistory: PricingInvoiceHistoryEntry[];
  lastCostInMicros: number | null;
  lastMarginPercentage: number | null;
  lastPriceInMicros: number | null;
  lineItems: number;
  priceDeltaInMicros: number | null;
  priceInMicros: number | null;
  restaurantUUID: string;
  supplierDisplayInfo: Record<string, PriceEditDisplayCell>;
  supplierUUID: string;
  variantPackId: string;
  variantPackName: string;
  variantPackUOM: string;
  variantPackUUID: string;
  weeklyPurchaseQuantities: WeeklyPurchaseQuantity[];
}

export interface PricingCustomerDetailHeader {
  lineItems: number;
  marginPercentage: number | null;
  orderCount: number;
  priceFloorEnabled: boolean;
  priceFloorMode: VariantPackCostType | null;
  restaurantAccountId: string;
  restaurantName: string;
  restaurantUUID: string;
}

const transformDetailRow = (
  row: CustomerDetailRowFieldsFragment,
): PricingCustomerDetailRow | null => {
  const variantPack = row.variant_pack;
  if (!variantPack?.uuid || !row.restaurant_id) {
    return null;
  }

  // last_* come from the customer's most recent invoice of the item.
  const lastCostInMicros = row.last_cost_in_micros ?? null;

  // Deltas come from the backend, measured over the supplier's configured
  // lookback window (cost is gated to real cost changes; price/GM are ungated
  // actual movement). GM% delta is current - previous (both server-provided).
  const priceDeltaInMicros = row.price_in_micros_delta;
  const costDeltaInMicros = row.cost_in_micros_delta;
  const grossMarginDeltaInMicros = row.gross_margin_delta_in_micros;

  const grossMarginPercentageDelta =
    isValidNumber(row.current_margin_percentage) &&
    isValidNumber(row.previous_margin_percentage)
      ? row.current_margin_percentage - row.previous_margin_percentage
      : null;

  const grossProfitImpactInMicros = isValidNumber(grossMarginDeltaInMicros)
    ? grossMarginDeltaInMicros * row.line_items_total
    : null;

  return {
    costDeltaInMicros,
    costInMicros: row.current_cost_in_micros ?? null,
    // No edit on a freshly transformed row.
    editsGrossProfitImpactInMicros: 0,
    grossMarginDeltaInMicros,
    grossMarginPercentage: row.current_margin_percentage ?? null,
    grossMarginPercentageDelta,
    grossProfitImpactInMicros,
    grossProfitPerUnitInMicros: row.gross_profit_per_unit_in_micros ?? null,
    invoiceHistory: toPricingInvoiceHistory(row.invoice_history),
    lastCostInMicros,
    lastMarginPercentage: row.last_margin_percentage ?? null,
    lastPriceInMicros: row.last_price_in_micros ?? null,
    lineItems: row.line_items_total,
    priceDeltaInMicros,
    priceInMicros: row.current_price_in_micros ?? null,
    restaurantUUID: row.restaurant_id,
    supplierDisplayInfo: toPriceEditDisplayMap(row.price_edit_display_info),
    supplierUUID: row.supplier_id,
    variantPackId: variantPack.external_item_id ?? '',
    variantPackName: variantPack.item?.display_name ?? '',
    variantPackUOM: variantPack.pack?.unit
      ? getLocalizedPackUnit(variantPack.pack.unit as PackUnit)
      : '--',
    variantPackUUID: variantPack.uuid,
    weeklyPurchaseQuantities: row.weekly_purchase_quantities.map((w) => ({
      endDate: w.end_date,
      quantity: w.quantity,
      startDate: w.start_date,
    })),
  };
};

const transformDetailHeader = (
  header: CustomerDetailHeaderFieldsFragment,
): PricingCustomerDetailHeader => ({
  lineItems: header.line_items_total,
  marginPercentage: header.margin_percentage ?? null,
  orderCount: header.order_count,
  priceFloorEnabled: header.price_floor_enabled,
  priceFloorMode: header.price_floor_mode ?? null,
  restaurantAccountId: header.restaurant_account_id ?? '',
  restaurantName: header.restaurant?.name ?? '',
  restaurantUUID: header.restaurant_id,
});

export interface PricingCustomerDetail {
  header: PricingCustomerDetailHeader | null;
  rows: PricingCustomerDetailRow[];
}

export const useCustomerDetailTransformer = (
  data: PricingCustomerDetailDataQuery | undefined,
): PricingCustomerDetail => {
  return useMemo(() => {
    const response = data?.getCustomerDetailData;
    if (!response) {
      return { header: null, rows: [] };
    }
    return {
      header: transformDetailHeader(response.header),
      rows: response.rows
        .map(transformDetailRow)
        .filter((row): row is PricingCustomerDetailRow => row !== null),
    };
  }, [data]);
};

export const applyEditToCustomerDetailRow = (
  row: PricingCustomerDetailRow,
  edit: PricingEdit,
): PricingCustomerDetailRow => ({
  ...row,
  editsGrossProfitImpactInMicros: edit.grossProfitImpactInMicros,
  grossMarginPercentage: edit.grossMarginPercentage,
  grossMarginPercentageDelta: edit.grossMarginPercentageDelta,
  grossProfitImpactInMicros: edit.grossProfitImpactInMicros,
  grossProfitPerUnitInMicros: edit.grossProfitInMicros,
  priceDeltaInMicros: edit.priceDeltaInMicros,
  priceInMicros: edit.priceInMicros,
});

export const adaptCustomerDetailRowForCostChanges = (
  row: PricingCustomerDetailRow,
): PricingAccountRow => {
  const currentGPPerUnit =
    row.grossProfitPerUnitInMicros ??
    PricingCalculations.grossProfitAmountFromPrice(
      row.priceInMicros,
      row.costInMicros,
    ) ??
    0;

  // Per-unit GP movement over the lookback window (server-provided), so it
  // stays consistent with the cost-change view.
  const grossProfitDeltaInMicros = row.grossMarginDeltaInMicros ?? 0;

  return {
    // CC and Net GP Impacts are not used for customer list or detail
    // tables but set to 0 to satisfy typing
    costChangeGrossProfitImpactInMicros: 0,
    costDeltaInMicros: row.costDeltaInMicros ?? 0,
    costInMicros: row.costInMicros ?? 0,
    editsGrossProfitImpactInMicros: 0,
    grossMarginPercentage: row.grossMarginPercentage ?? 0,
    grossMarginPercentageDelta: row.grossMarginPercentageDelta ?? 0,
    grossProfitDeltaInMicros,
    grossProfitImpactInMicros: row.grossProfitImpactInMicros ?? 0,
    grossProfitInMicros: currentGPPerUnit,
    groups: [],
    invoiceHistory: row.invoiceHistory,
    isLocked: false,
    netGrossProfitImpactInMicros: 0,
    peerRange: null,
    priceDeltaInMicros: row.priceDeltaInMicros ?? 0,
    priceFloorEnabled: false,
    priceFloorMode: null,
    priceInMicros: row.priceInMicros ?? 0,
    quantity: row.lineItems,
    restaurantAccountId: '',
    restaurantName: '',
    restaurantUUID: row.restaurantUUID,
    supplierDisplayInfo: row.supplierDisplayInfo,
    supplierUUID: row.supplierUUID,
    variantPackId: row.variantPackId,
    variantPackName: row.variantPackName,
    variantPackUOM: row.variantPackUOM,
    variantPackUUID: row.variantPackUUID,
    weeklyPurchaseQuantities: row.weeklyPurchaseQuantities,
  };
};
